import { ApolloClient } from '@apollo/client'
import { NextRouter } from 'next/router'

import { getRequiredPages, RequirePages } from './webInductionHelper'

const getBackProjectRoute = (projectId: string) =>
  `/public/project/${projectId}`

export const baseWorkerWebUrls = (projectId: string) => {
  const baseProjectRoute = getBackProjectRoute(projectId)
  const baseInductionRoute = `/web/project/${projectId}/induction`

  const baseVisitorInductionRoute = `${baseInductionRoute}/visitor`
  const baseCompanyInductionRoute = `${baseInductionRoute}/company`
  const baseSiteInductionRoute = `${baseInductionRoute}/site`
  const baseTimesheetsRoute = `/web/timesheets`
  const baseDefectsRoute = `/web/defects`
  const baseChecklistsRoute = `/web/checklists`
  const basePermitsRoute = `/web/permits`
  const baseNotificationsPreferencesRoute = `/web/notifications`
  const baseRegistersRoute = `/web/registers`
  const baseDrawingsRoute = `/web/drawings`
  return {
    baseProjectRoute,
    baseInductionRoute,
    baseSiteInductionRoute,
    baseVisitorInductionRoute,
    baseCompanyInductionRoute,
    baseTimesheetsRoute,
    baseChecklistsRoute,
    basePermitsRoute,
    baseNotificationsPreferencesRoute,
    baseDefectsRoute,
    baseRegistersRoute,
    baseDrawingsRoute,
    appOrWeb: baseProjectRoute + '/worker',
  }
}

export const goTo = (
  { push }: NextRouter,
  projectId: string,
  client: ApolloClient<object>
) => {
  const {
    baseProjectRoute,
    baseInductionRoute,
    baseSiteInductionRoute,
    baseCompanyInductionRoute,
    baseVisitorInductionRoute,
    baseTimesheetsRoute,
    baseChecklistsRoute,
    basePermitsRoute,
    baseDefectsRoute,
    baseNotificationsPreferencesRoute,
    baseRegistersRoute,
    baseDrawingsRoute,
    appOrWeb,
  } = baseWorkerWebUrls(projectId)

  const skipSiteInductionForward = (requirePages: RequirePages) => {
    const { siteInduction, disabledSiteInduction } = requirePages

    if (disabledSiteInduction) {
      return push(baseSiteInductionRoute + '/disabled-site-induction')
    }

    if (siteInduction) {
      return push(baseSiteInductionRoute + '/site-induction')
    }

    return skipSignOnForward(requirePages)
  }

  const skipCompanyInductionForward = (requirePages: RequirePages) => {
    const { companyInduction } = requirePages

    if (companyInduction) {
      return push(baseSiteInductionRoute + '/company-induction')
    }

    return skipSwmsForward(requirePages)
  }

  const skipTicketsForward = (requirePages: RequirePages) => {
    const { tickets } = requirePages

    if (tickets) {
      return push(baseSiteInductionRoute + '/tickets')
    }

    return skipCompanyInductionForward(requirePages)
  }

  const skipTicketsBackwards = (requirePages: RequirePages) => {
    const { tickets } = requirePages

    if (tickets) {
      return push(baseSiteInductionRoute + '/tickets')
    }
    push(baseSiteInductionRoute + '/emergency')
  }

  const skipSwmsForward = (requirePages: RequirePages) => {
    const { swms, disabledSiteInduction } = requirePages

    if (disabledSiteInduction) {
      return push(baseSiteInductionRoute + '/disabled-site-induction')
    }

    if (swms) {
      return push(baseSiteInductionRoute + '/swms')
    }

    return skipSiteInductionForward(requirePages)
  }

  const skipSwmsBackward = (requirePages: RequirePages) => {
    const { swms } = requirePages

    if (swms) {
      return push(baseSiteInductionRoute + '/swms')
    }
    return skipCompanyInductionBackward(requirePages)
  }

  const skipCompanyInductionBackward = (requirePages: RequirePages) => {
    const { companyInduction } = requirePages

    if (companyInduction) {
      return push(baseSiteInductionRoute + '/company-induction')
    }

    return skipTicketsBackwards(requirePages)
  }

  const skipSiteInductionBackward = (requirePages: RequirePages) => {
    const { siteInduction } = requirePages

    if (siteInduction) {
      return push(baseSiteInductionRoute + '/site-induction')
    }

    return skipSwmsBackward(requirePages)
  }

  const skipSignOnForward = (requirePages: RequirePages) => {
    const { signOn } = requirePages

    if (signOn) {
      return push(baseSiteInductionRoute + '/sign-on')
    }
    return push(
      baseSiteInductionRoute +
        '/success' +
        (projectId ? `?projectId=${projectId}` : '')
    )
  }

  const goToRoutes = {
    urls: {
      publicProjectIndex: baseProjectRoute,
      enterProjectCode: baseProjectRoute + '/enter-project-code',
      appOrWeb,
      landing: (projectId) =>
        `${baseInductionRoute}/landing${
          projectId ? `?projectId=${projectId}` : ''
        }`,
      timesheets: {
        index: (projectId: string) =>
          `${baseTimesheetsRoute}${projectId ? `?projectId=${projectId}` : ''}`,
        list: (projectId: string) =>
          `${baseTimesheetsRoute}/list${
            projectId ? `?projectId=${projectId}` : ''
          }`,
        new: (projectId: string) =>
          `${baseTimesheetsRoute}/new${
            projectId ? `?projectId=${projectId}` : ''
          }`,
      },
      defects: {
        index: (projectId: string) =>
          `${baseDefectsRoute}${projectId ? `?projectId=${projectId}` : ''}`,
        list: (projectId: string) =>
          `${baseDefectsRoute}/list${
            projectId ? `?projectId=${projectId}` : ''
          }`,
      },
      drawings: {
        list: (projectId: string) => `${baseDrawingsRoute}/${projectId}/list`,
        index: (projectId: string) => `${baseDrawingsRoute}/${projectId}`,
      },
      checklists: {
        index: (projectId: string) =>
          `${baseChecklistsRoute}${projectId ? `/${projectId}` : ''}`,
        list: (projectId: string) =>
          `${baseChecklistsRoute}${projectId ? `/${projectId}` : ''}/list`,
        new: (projectId: string, checklisId: string) =>
          `${baseChecklistsRoute}${
            projectId ? `/${projectId}` : ''
          }/${checklisId}`,
      },
      registers: {
        index: (projectId: string) =>
          `${baseRegistersRoute}${projectId ? `/${projectId}` : ''}`,
        list: (projectId: string) =>
          `${baseRegistersRoute}${projectId ? `/${projectId}` : ''}/list`,
      },
      permits: {
        list: (projectId: string) =>
          `${basePermitsRoute}/list${
            projectId ? `?projectId=${projectId}` : ''
          }`,
      },
      notificationsPreferences: {
        index: (projectId: string) =>
          `${baseNotificationsPreferencesRoute}?projectId=${projectId}`,
      },
    },
    common: {
      workerOrVisitor: () => {
        push(baseProjectRoute)
      },
      success: (signedOff?: boolean) => {
        const query = signedOff ? '?signedOff=true' : ''
        push(baseInductionRoute + '/success' + query)
      },
    },
    worker: {
      appOrWeb: () => {
        push(appOrWeb)
      },
      profile: () => {
        push(baseSiteInductionRoute + '/profile')
      },
      contact: () => {
        push(baseSiteInductionRoute + '/contact')
      },
      emergency: () => {
        push(baseSiteInductionRoute + '/emergency')
      },
      job: () => {
        push(baseSiteInductionRoute + '/job')
      },
      tickets: async (
        isForward: boolean,
        skipChecks: boolean,
        requiredPages?: RequirePages
      ) => {
        if (skipChecks) {
          return push(baseSiteInductionRoute + '/tickets')
        }

        const pages =
          requiredPages ?? (await getRequiredPages(client, projectId))

        if (isForward) {
          skipTicketsForward(pages)
        } else {
          skipTicketsBackwards(pages)
        }
      },
      swms: async (isForward: boolean, requiredPages?: RequirePages) => {
        const pages =
          requiredPages ?? (await getRequiredPages(client, projectId))

        if (isForward) {
          skipSwmsForward(pages)
        } else {
          skipSwmsBackward(pages)
        }
      },
      companyInduction: async (isForward: boolean) => {
        const requiredPages = await getRequiredPages(client, projectId)

        if (isForward) {
          skipCompanyInductionForward(requiredPages)
        } else {
          skipCompanyInductionBackward(requiredPages)
        }
      },
      siteInduction: async (isForward: boolean) => {
        const requiredPages = await getRequiredPages(client, projectId)

        if (isForward) {
          skipSiteInductionForward(requiredPages)
        } else {
          skipSiteInductionBackward(requiredPages)
        }
      },
      signOn: async () => {
        const requiredPages = await getRequiredPages(client, projectId)
        skipSignOnForward(requiredPages)
      },
    },
    visitor: {
      profile: () => {
        push(baseVisitorInductionRoute + '/profile')
      },
      signOn: () => {
        push(baseVisitorInductionRoute + '/sign-on')
      },
    },
    company: {
      redirect: () => {
        push(baseCompanyInductionRoute)
      },
      profile: () => {
        push(baseCompanyInductionRoute + '/profile')
      },
      contact: () => {
        push(baseCompanyInductionRoute + '/contact')
      },
      job: () => {
        push(baseCompanyInductionRoute + '/job')
      },
      emergency: () => {
        push(baseCompanyInductionRoute + '/emergency')
      },
      tickets: () => {
        push(baseCompanyInductionRoute + '/tickets')
      },
      companyInduction: async () => {
        const requiredPages = await getRequiredPages(client, projectId)

        if (requiredPages.companyInduction) {
          push(baseCompanyInductionRoute + '/company-induction')
        } else {
          push(baseCompanyInductionRoute + '/success')
        }
      },
      success: () => {
        push(baseCompanyInductionRoute + '/success')
      },
    },
    skip: {
      worker: null,
    },
  }

  goToRoutes.skip.worker = async () => {
    const requiredPages = await getRequiredPages(client, projectId)

    if (requiredPages.siteInduction) {
      return goToRoutes.worker.profile() // if missing site induction, go to start
    }

    // go to swms which may skip swms/company induction/site induction
    return goToRoutes.worker.tickets(true, false, requiredPages)
  }

  return goToRoutes
}
